import axios from "axios";
import { invalidateClientCaches, resetClientCaches } from "./clientCache";
import { buildListQueryParams } from "./listQueryParams";

const api = axios.create({
  baseURL: "/server/assessment_function",
  headers: {
    "Content-Type": "application/json"
  }
});

export const permissionAPI = {
  getCatalog: () => api.get("/permissions/catalog"),
  getMe: () => api.get("/permissions/me"),
  getMatrix: () => api.get("/permissions/matrix"),
  updateMatrix: (data) => api.put("/permissions/matrix", data),
  seed: () => api.post("/permissions/seed"),
};

export const authAPI = {
  /** @deprecated Use getMe() session bootstrap instead */
  syncUser: () => api.post("/users/signup-sync"),
  getMe: () => api.get("/users/me"),
  getPending: () => api.get("/users/pending"),
  approve: (data, config) => api.post("/users/approve", data, config),
  inviteUser: (data) => api.post("/users/invite", data),
  inviteAdmin: (data) => api.post("/users/invite-admin", data),
  inviteHrAdmin: (data) => api.post("/users/invite-hr-admin", data),
  inviteEmployee: (data) => api.post("/users/invite-employee", data),
  list: () => api.get("/users/list"),
  listAll: (orgId) =>
    api.get("/users/all", orgId ? { params: { orgId } } : undefined),
  listCandidates: (orgId, options = {}) =>
    api.get("/users/candidates", {
      params: buildListQueryParams({
        ...(orgId ? { orgId } : {}),
        ...(options?.includeUserInfo ? { include_user_info: "1" } : {}),
        page: options.page ?? 1,
        limit: options.limit ?? 25,
        search: options.search,
        sortBy: options.sortBy,
        sortOrder: options.sortOrder,
        filters: options.filters,
        criteria: options.criteria,
      }),
    }),
  getCandidateImportSettings: () => api.get("/users/import-settings"),
  bulkInviteCandidates: (data) => api.post("/users/bulk-invite", data),
  createCandidateImportJob: (data) => api.post("/users/import-jobs", data),
  getCandidateImportJob: (jobId) => api.get(`/users/import-jobs/${jobId}`),
  listCandidateImportJobRows: (jobId, params) =>
    api.get(`/users/import-jobs/${jobId}/rows`, { params }),
  processCandidateImportJob: (jobId) => api.post(`/users/import-jobs/${jobId}/process`),
  cancelCandidateImportJob: (jobId) => api.post(`/users/import-jobs/${jobId}/cancel`),
  listStaff: (orgId, params = {}) =>
    api.get("/users/staff", { params: { ...(orgId ? { orgId } : {}), ...params } }),
  getUserGrowth: (months = 12) =>
    api.get("/users/analytics/growth", { params: { months } }),
  setUserStatus: (data, config) => api.post("/users/set-status", data, config),
  deleteCandidate: (rowId, data, config) =>
    api.post(`/users/${rowId}/delete-candidate`, data || {}, config),
  getById: (rowId) => api.get(`/users/${rowId}`),
  getUserDetail: (rowId) => api.get(`/users/${rowId}/detail`),
};

export function getApiErrorCode(err) {
  const body = err?.response?.data;
  return body?.error?.code || body?.code || null;
}

function isTransientServerError(err) {
  const status = err?.response?.status;
  return status === 502 || status === 503 || status === 504 || !status;
}

let suspendedRefreshTimer = null;

const MUTATION_METHODS = new Set(["post", "put", "patch", "delete"]);

function normalizeMutationPath(url) {
  const path = String(url || "").split("?")[0].replace(/\/+$/, "") || "/";
  return path.startsWith("/") ? path : `/${path}`;
}

export function extractBankIdFromBody(body) {
  if (body == null) return undefined;
  if (typeof FormData !== "undefined" && body instanceof FormData) return undefined;

  let parsed = body;
  if (typeof body === "string") {
    try {
      parsed = JSON.parse(body);
    } catch {
      return undefined;
    }
  }
  if (typeof parsed !== "object" || parsed === null) return undefined;

  const bankId = parsed.bank_id ?? parsed.bankId;
  if (bankId == null || bankId === "") return undefined;
  return String(bankId).trim();
}

function ruleMatches(rule, method, path) {
  if (!rule.methods.includes(method)) return false;
  if (!rule.pattern.test(path)) return false;
  if (rule.exclude?.test(path)) return false;
  return true;
}

const TELEMETRY_RULES = [
  { methods: ["post"], pattern: /^\/face-interview\/(analyze|start|stop)$/ },
  { methods: ["post"], pattern: /^\/assessments\/heartbeat$/ },
  { methods: ["post"], pattern: /^\/exam-invite\/session\/heartbeat$/ },
  { methods: ["post"], pattern: /^\/assessments\/integrity-violation$/ },
  { methods: ["post"], pattern: /^\/exam-invite\/session\/integrity-violation$/ },
];

const DATA_RULES = [
  {
    methods: ["post"],
    pattern: /^\/questions\/banks\/create$/,
    tags: ["banks"],
  },
  {
    methods: ["patch"],
    pattern: /^\/questions\/banks\/[^/]+$/,
    tags: ["banks"],
  },
  {
    methods: ["post"],
    pattern: /^\/questions\/banks\/delete$/,
    tags: ["banks", "bankQuestions"],
    getBankId: extractBankIdFromBody,
  },
  {
    methods: ["post"],
    pattern: /^\/questions\/create$/,
    tags: ["bankQuestions", "banks"],
    getBankId: extractBankIdFromBody,
  },
  {
    methods: ["patch"],
    pattern: /^\/questions\/[^/]+$/,
    tags: ["bankQuestions", "banks"],
    getBankId: extractBankIdFromBody,
  },
  {
    methods: ["post"],
    pattern: /^\/questions\/bulk-create$/,
    tags: ["bankQuestions", "banks"],
    getBankId: extractBankIdFromBody,
  },
  {
    methods: ["post"],
    pattern: /^\/questions\/delete$/,
    tags: ["bankQuestions", "banks"],
    getBankId: extractBankIdFromBody,
  },
  {
    methods: ["post", "patch"],
    pattern: /^\/questions\/case-study-group\//,
    tags: ["bankQuestions"],
    getBankId: extractBankIdFromBody,
  },
  {
    methods: ["post"],
    pattern: /^\/users\/(approve|invite|invite-admin|invite-hr-admin|invite-employee|bulk-invite|set-status)$/,
    tags: ["search:users"],
  },
  {
    methods: ["post"],
    pattern: /^\/users\/[^/]+\/delete-candidate$/,
    tags: ["search:users"],
  },
  {
    methods: ["post"],
    pattern: /^\/users\/import-jobs\/[^/]+\/process$/,
    tags: ["search:users"],
  },
  {
    methods: ["patch"],
    pattern: /^\/user-info\/[^/]+$/,
    tags: ["search:users"],
  },
  {
    methods: ["post"],
    pattern: /^\/assessments\/create$/,
    tags: ["search:assessments"],
  },
  {
    methods: ["patch"],
    pattern: /^\/assessments\/[^/]+$/,
    tags: ["search:assessments"],
  },
  {
    methods: ["post"],
    pattern: /^\/assessments\/[^/]+\/(delete|publish|request-approval|approve-publish|reject-publish)$/,
    tags: ["search:assessments"],
  },
  {
    methods: ["post"],
    pattern: /^\/assessments\/[^/]+\/assign$/,
    tags: ["search:myAssignments"],
  },
  {
    methods: ["post"],
    pattern: /^\/assessment-cycles\/[^/]+\/assign$/,
    tags: ["search:myAssignments"],
  },
  {
    methods: ["post"],
    pattern: /^\/assessments\/(start|submit)$/,
    tags: ["search:myAssignments"],
  },
];

const NO_OP_RULES = [
  {
    methods: ["post", "patch", "delete"],
    pattern: /^\/assessment-cycles(?:\/|$)/,
    exclude: /\/assign$/,
  },
  { methods: ["post", "patch", "delete"], pattern: /^\/jobs(?:\/|$)/ },
  { methods: ["post", "patch", "delete"], pattern: /^\/candidate-groups(?:\/|$)/ },
  { methods: ["post", "patch", "delete"], pattern: /^\/organizations(?:\/|$)/ },
  { methods: ["post", "put", "patch", "delete"], pattern: /^\/permissions(?:\/|$)/ },
  { methods: ["post", "patch"], pattern: /^\/users\/me\/quick-actions$/ },
  { methods: ["post"], pattern: /^\/users\/import-jobs$/ },
  { methods: ["post"], pattern: /^\/users\/import-jobs\/[^/]+\/cancel$/ },
  { methods: ["post"], pattern: /^\/exam-invite\/(send|validate)$/ },
  {
    methods: ["post"],
    pattern: /^\/exam-invite\/session\/(start|submit|feedback)$/,
  },
  {
    methods: ["post"],
    pattern: /^\/assessments\/assignments\/[^/]+\/(candidate-feedback|send-feedback)$/,
  },
  { methods: ["post"], pattern: /^\/users\/signup-sync$/ },
];

/**
 * Decide how to invalidate client caches after a mutation response.
 * @returns {{ action: "none" | "tags" | "resetAll", tags?: string[], bankId?: string }}
 */
export function resolveCacheInvalidation(method, url, body) {
  const normalizedMethod = String(method || "").toLowerCase();
  const path = normalizeMutationPath(url);

  if (TELEMETRY_RULES.some((rule) => ruleMatches(rule, normalizedMethod, path))) {
    return { action: "none" };
  }

  for (const rule of DATA_RULES) {
    if (!ruleMatches(rule, normalizedMethod, path)) continue;
    const bankId = rule.getBankId?.(body);
    return {
      action: "tags",
      tags: rule.tags,
      ...(bankId ? { bankId } : {}),
    };
  }

  if (NO_OP_RULES.some((rule) => ruleMatches(rule, normalizedMethod, path))) {
    return { action: "none" };
  }

  return { action: "resetAll" };
}

function applyCacheInvalidation(response) {
  const method = String(response.config?.method || "").toLowerCase();
  if (!MUTATION_METHODS.has(method)) return;

  const url = response.config?.url || "";
  const body = response.config?.data;
  const result = resolveCacheInvalidation(method, url, body);

  if (result.action === "tags") {
    invalidateClientCaches(result.tags, { bankId: result.bankId });
    return;
  }

  if (result.action === "resetAll") {
    const isDev =
      (typeof import.meta !== "undefined" && import.meta.env?.DEV) ||
      process.env.NODE_ENV === "development";
    if (isDev) {
      console.warn(
        `[cache] No invalidation rule for ${method.toUpperCase()} ${url}; resetting all client caches. Add a rule in resolveCacheInvalidation.`
      );
    }
    resetClientCaches();
  }
}

api.interceptors.response.use(
  (response) => {
    applyCacheInvalidation(response);
    return response;
  },
  (error) => {
    if (getApiErrorCode(error) === "ACCOUNT_SUSPENDED") {
      if (!suspendedRefreshTimer) {
        suspendedRefreshTimer = window.setTimeout(() => {
          suspendedRefreshTimer = null;
          window.dispatchEvent(new CustomEvent("evalris:account-suspended"));
        }, 300);
      }
    }
    return Promise.reject(error);
  }
);

export { isTransientServerError };

export const questionAPI = {
  getSettings: () => api.get("/questions/settings"),
  getBanks: (params) => api.get("/questions/banks", { params }),
  getGeneralBanks: () => api.get("/questions/banks/general"),
  createBank: (data) => api.post("/questions/banks/create", data),
  updateBank: (bankId, data) => api.patch(`/questions/banks/${bankId}`, data),
  getQuestions: (bankId, params) => api.get(`/questions/list?bank_id=${bankId}`, { params }),
  createQuestion: (data) =>
    api.post("/questions/create", data, { timeout: 60000 }),
  updateQuestion: (questionId, data) =>
    api.patch(`/questions/${questionId}`, data, { timeout: 60000 }),
  bulkCreateQuestions: (data) =>
    api.post("/questions/bulk-create", data, { timeout: 120000 }),
  createCaseStudyGroup: (data) =>
    api.post("/questions/case-study-group/create", data, { timeout: 120000 }),
  linkCaseStudyGroup: (data) => api.post("/questions/case-study-group/link", data),
  unlinkCaseStudyGroup: (data) => api.post("/questions/case-study-group/unlink", data),
  reorderCaseStudyGroup: (data) => api.patch("/questions/case-study-group/reorder", data),
  updateCaseStudyGroup: (data) => api.patch("/questions/case-study-group/update", data, { timeout: 120000 }),
  deleteQuestion: (data) => api.post("/questions/delete", data),
  deleteBank: (data) => api.post("/questions/banks/delete", data),
};

function normalizeAssignmentId(value) {
  if (value == null || value === "") return "";
  return String(value).trim();
}

const EXAM_INSTANCE_HEADER = "X-Exam-Session-Id";

function examInstanceHeaders(examSessionId) {
  const id = String(examSessionId || "").trim();
  return id ? { [EXAM_INSTANCE_HEADER]: id } : {};
}

function examInstanceBody(examSessionId, body = {}) {
  const id = String(examSessionId || "").trim();
  return id ? { ...body, exam_session_id: id } : body;
}

export const assessmentAPI = {
  create: (data) => api.post("/assessments/create", data),
  list: (options = {}) =>
    api.get("/assessments/list", {
      params: buildListQueryParams({
        ...(options.includeInactive ? { include_inactive: true } : {}),
        page: options.page,
        limit: options.limit,
        search: options.search,
        sortBy: options.sortBy,
        sortOrder: options.sortOrder,
        filters: options.filters,
      }),
    }),
  patch: (id, data, config) => api.patch(`/assessments/${id}`, data, config),
  delete: (id, config) => api.post(`/assessments/${id}/delete`, undefined, config),
  publish: (id, config) => api.post(`/assessments/${id}/publish`, undefined, config),
  requestApproval: (id, config) => api.post(`/assessments/${id}/request-approval`, undefined, config),
  approvePublish: (id, data, config) => api.post(`/assessments/${id}/approve-publish`, data || {}, config),
  rejectPublish: (id, data, config) => api.post(`/assessments/${id}/reject-publish`, data, config),
  getHistory: (id) => api.get(`/assessments/${id}/history`),
  listPendingApproval: () => api.get("/assessments/pending-approval"),
  assign: (id, data) => api.post(`/assessments/${id}/assign`, data),
  getAssessmentSelection: (assessmentId) => api.get(`/assessments/${assessmentId}/questions`),
  getAssessmentBanks: (assessmentId) => api.get(`/assessments/${assessmentId}/banks`),
  getMyAssignments: () => api.get("/assessments/my-assignments"),
  getAllAssignments: (params) => api.get("/assessments/all-assignments", { params }),
  getAssessmentsOverTime: (months = 6) =>
api.get("/assessments/analytics/over-time", { params: { months } }),
  getDetails: (id) => api.get(`/assessments/assignment/${normalizeAssignmentId(id)}`),
  getSubmissionReview: (id) =>
    api.get(`/assessments/assignment/${normalizeAssignmentId(id)}/submission-review`),
  getQuestions: (id) => api.get(`/assessments/assignment/${normalizeAssignmentId(id)}/questions`),
  start: (id, examSessionId) =>
    api.post(
      "/assessments/start",
      examInstanceBody(examSessionId, { assignment_id: normalizeAssignmentId(id) }),
      { headers: examInstanceHeaders(examSessionId) }
    ),
  heartbeat: (assignmentId, telemetry, examSessionId) =>
    api.post(
      "/assessments/heartbeat",
      examInstanceBody(examSessionId, {
        assignment_id: normalizeAssignmentId(assignmentId),
        telemetry,
      }),
      { headers: examInstanceHeaders(examSessionId) }
    ),
  submit: (id, answers) =>
    api.post("/assessments/submit", { assignment_id: normalizeAssignmentId(id), answers }),
  submitCandidateFeedback: (assignmentId, feedback) =>
    api.post(`/assessments/assignments/${normalizeAssignmentId(assignmentId)}/candidate-feedback`, { feedback }),
  reportIntegrityViolation: (assignmentId, type) =>
    api.post("/assessments/integrity-violation", {
      assignment_id: normalizeAssignmentId(assignmentId),
      type,
    }),
  getExamIntegritySettings: () => api.get("/assessments/exam-integrity-settings"),
  getExamOptionSettings: () => api.get("/assessments/exam-option-settings"),
  sendFeedbackEmail: (assignmentId, data) =>
    api.post(`/assessments/assignments/${assignmentId}/send-feedback`, data)
};

export const assessmentCycleAPI = {
  list: (params = {}) => api.get("/assessment-cycles", { params: buildListQueryParams(params) }),
  listPendingApproval: () => api.get("/assessment-cycles/pending-approval"),
  getById: (id) => api.get(`/assessment-cycles/${id}`),
  listAssignments: (id, params = {}) =>
    api.get(`/assessment-cycles/${id}/assignments`, {
      params: buildListQueryParams({
        page: params.page ?? 1,
        limit: params.limit ?? 25,
        search: params.search,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
        filters: params.filters,
      }),
    }),
  listUnassignedCandidates: (id, params = {}) =>
    api.get(`/assessment-cycles/${id}/unassigned-candidates`, {
      params: buildListQueryParams({
        page: params.page ?? 1,
        limit: params.limit ?? 25,
        ...(params.includeUserInfo !== false ? { include_user_info: "1" } : {}),
        search: params.search,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
        filters: params.filters,
        criteria: params.criteria,
      }),
    }),
  create: (data) => api.post("/assessment-cycles", data),
  update: (id, data, config) => api.patch(`/assessment-cycles/${id}`, data, config),
  clone: (id, config) => api.post(`/assessment-cycles/${id}/clone`, undefined, config),
  requestApproval: (id, config) =>
    api.post(`/assessment-cycles/${id}/request-approval`, undefined, config),
  approve: (id, data, config) => api.post(`/assessment-cycles/${id}/approve`, data || {}, config),
  reject: (id, data, config) => api.post(`/assessment-cycles/${id}/reject`, data || {}, config),
  archive: (id, config) => api.post(`/assessment-cycles/${id}/archive`, undefined, config),
  restore: (id, config) => api.post(`/assessment-cycles/${id}/restore`, undefined, config),
  assign: (id, data, config) => api.post(`/assessment-cycles/${id}/assign`, data, config),
};

export const jobAPI = {
  list: (params = {}) => api.get("/jobs", { params }),
  getById: (id) => api.get(`/jobs/${id}`),
  create: (data) => api.post("/jobs", data),
  update: (id, data) => api.patch(`/jobs/${id}`, data),
  remove: (id) => api.delete(`/jobs/${id}`),
};

export const userInfoAPI = {
  get: (userId) => api.get(`/user-info/${userId}`),
  update: (userId, data, config) => api.patch(`/user-info/${userId}`, data, config),
};

export const userPreferencesAPI = {
  getQuickActions: () => api.get("/users/me/quick-actions"),
  updateQuickActions: (quick_actions) => api.patch("/users/me/quick-actions", { quick_actions }),
};

export const candidateGroupAPI = {
  list: (params = {}) =>
    api.get("/candidate-groups", {
      params: buildListQueryParams({
        page: params.page ?? 1,
        limit: params.limit ?? 25,
        search: params.search,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
        filters: params.filters,
      }),
    }),
  listForCandidate: (userId) => api.get(`/candidate-groups/by-candidate/${userId}`),
  getById: (id) => api.get(`/candidate-groups/${id}`),
  create: (data) => api.post("/candidate-groups", data),
  update: (id, data) => api.patch(`/candidate-groups/${id}`, data),
  remove: (id) => api.delete(`/candidate-groups/${id}`),
  listMembers: (id, params = {}) =>
    api.get(`/candidate-groups/${id}/members`, {
      params: buildListQueryParams({
        page: params.page ?? 1,
        limit: params.limit ?? 25,
        search: params.search,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
        filters: params.filters,
      }),
    }),
  addMembers: (id, data) => api.post(`/candidate-groups/${id}/members`, data),
  removeMember: (id, userId) => api.delete(`/candidate-groups/${id}/members/${userId}`),
  addMembersByName: (data) => api.post("/candidate-groups/add-members", data),
};

export const examInviteAPI = {
  send: (data, config) => api.post("/exam-invite/send", data, config),
  getMeta: (inviteId) => api.get(`/exam-invite/${inviteId}/meta`),
  validate: (data) => api.post("/exam-invite/validate", data)
};

export const usageMetricsAPI = {
  getCurrent: (orgId) =>
    api.get("/usage-metrics/current", orgId ? { params: { orgId } } : undefined),
  getPlan: (orgId) =>
    api.get("/usage-metrics/plan", orgId ? { params: { orgId } } : undefined),
};

export const creditsAPI = {
  getBalance: (orgId) =>
    api.get("/credits/balance", orgId ? { params: { orgId } } : undefined),
};

export const organizationAPI = {
  list: () => api.get("/organizations"),
  getMe: () => api.get("/organizations/me"),
  updateMe: (data) => api.patch("/organizations/me", data),
  getById: (id) => api.get(`/organizations/${id}`),
  create: (data) => api.post("/organizations", data),
  rotateRecruitKey: (id, config) => api.post(`/organizations/${id}/recruit-key/rotate`, undefined, config),
};

function auditLogListParams(params = {}) {
  return {
    page: params.page ?? 1,
    limit: params.limit ?? 25,
    ...(params.entity_type ? { entity_type: params.entity_type } : {}),
    ...(params.entity_id ? { entity_id: params.entity_id } : {}),
    ...(params.event_type ? { event_type: params.event_type } : {}),
    ...(params.event_category ? { event_category: params.event_category } : {}),
    ...(params.actor_user_id ? { actor_user_id: params.actor_user_id } : {}),
    ...(params.from ? { from: params.from } : {}),
    ...(params.to ? { to: params.to } : {}),
    ...(params.org_id ? { org_id: params.org_id } : {}),
  };
}

export const auditLogsAPI = {
  list: (params) => api.get("/audit-logs", { params: auditLogListParams(params) }),
  listGlobal: (params) => api.get("/audit-logs/global", { params: auditLogListParams(params) }),
  listByEntity: (entityType, entityId, params = {}) =>
    api.get(
      `/audit-logs/entity/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`,
      { params: auditLogListParams(params) }
    ),
};

export const dashboardAPI = {
  getSummary: () => api.get("/dashboard/summary"),
  getEvalrisOverview: () => api.get("/dashboard/evalris-overview"),
};

const EXAM_SESSION_HEADER = "X-Exam-Session-Token";

function examSessionHeaders(sessionToken) {
  return { [EXAM_SESSION_HEADER]: sessionToken };
}

export const publicExamAPI = {
  getAssignment: (assignmentId, sessionToken) =>
    api.get(`/exam-invite/session/assignment/${assignmentId}`, {
      headers: examSessionHeaders(sessionToken)
    }),
  start: (assignmentId, sessionToken, examSessionId) =>
    api.post(
      "/exam-invite/session/start",
      examInstanceBody(examSessionId, { assignment_id: assignmentId }),
      { headers: { ...examSessionHeaders(sessionToken), ...examInstanceHeaders(examSessionId) } }
    ),
  heartbeat: (assignmentId, sessionToken, telemetry, examSessionId) =>
    api.post(
      "/exam-invite/session/heartbeat",
      examInstanceBody(examSessionId, { assignment_id: assignmentId, telemetry }),
      { headers: { ...examSessionHeaders(sessionToken), ...examInstanceHeaders(examSessionId) } }
    ),
  getQuestions: (assignmentId, sessionToken) =>
    api.get(`/exam-invite/session/questions/${assignmentId}`, {
      headers: examSessionHeaders(sessionToken)
    }),
  submit: (assignmentId, answers, sessionToken) =>
    api.post(
      "/exam-invite/session/submit",
      { assignment_id: assignmentId, answers },
      { headers: examSessionHeaders(sessionToken) }
    ),
  submitFeedback: (assignmentId, feedback, sessionToken) =>
    api.post(
      "/exam-invite/session/feedback",
      { assignment_id: assignmentId, feedback },
      { headers: examSessionHeaders(sessionToken) }
    ),
  reportIntegrityViolation: (assignmentId, type, sessionToken) =>
    api.post(
      "/exam-invite/session/integrity-violation",
      { assignment_id: assignmentId, type },
      { headers: examSessionHeaders(sessionToken) }
    )
};


const faceServiceApi = axios.create({
  baseURL: "/server/assessment_function/face-interview",
});

export const faceInterviewAPI = {
  start: () => faceServiceApi.post("/start"),
  stop: () => faceServiceApi.post("/stop"),
  status: () => faceServiceApi.get("/status"),
  health: () => faceServiceApi.get("/health"),
  analyze: (formData) =>
    faceServiceApi.post("/analyze", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
};

export default api;